/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 📊 Chart legend "isolate" click handler — drop into Chart.js's
/// `plugins.legend.onClick` so every chart in the app gets the same
/// shortcut for "show only this series".
///
/// UX:
///   - Single click  → default Chart.js toggle (hide / show that series)
///   - Double click  → isolate that series: hide everything else
///   - Double click on the already-isolated series → restore all
///
/// Why double-click instead of changing single-click: users with
/// muscle memory for Chart.js legends expect a single click to toggle
/// one series at a time. Replacing that would surprise people who
/// know the default. Layering double-click on top adds the "show only
/// this one" affordance without removing the existing behavior.
///
/// Implementation note: Chart.js doesn't fire a synthetic dblclick on
/// the legend, but it DOES fire onClick for each click. We detect the
/// double-click by tracking the previous click's (index, time) per
/// instance — passed in as captured state by the factory below.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const DOUBLE_CLICK_MS = 350

/**
 * Factory that returns a legend `onClick` handler. Each chart needs
 * its own handler instance so the click-tracking state stays
 * per-chart (otherwise a click on chart A would race a click on
 * chart B and incorrectly fire as a double-click).
 */
export function createIsolatingLegendClickHandler() {
  let lastClickIndex = -1
  let lastClickTime = 0

  return function onLegendClick(_e: any, legendItem: any, legend: any) {
    const chart = legend.chart
    const datasets = chart?.data?.datasets || []
    if (!datasets.length) return

    // Chart.js uses `datasetIndex` for non-pie charts (Line/Bar) and
    // `index` for pies / doughnuts. Prefer datasetIndex when present.
    const idx: number = legendItem.datasetIndex ?? legendItem.index ?? -1
    if (idx < 0) return

    const now = Date.now()
    const isDouble = idx === lastClickIndex && (now - lastClickTime) < DOUBLE_CLICK_MS
    lastClickIndex = idx
    lastClickTime = now

    if (isDouble) {
      // Isolate / un-isolate. If the clicked series is already the
      // ONLY visible one, restore everything. Otherwise hide the
      // others and show this one.
      const visibleIndices = datasets
        .map((_d: any, i: number) => i)
        .filter((i: number) => chart.isDatasetVisible(i))
      const isAlreadyIsolated = visibleIndices.length === 1 && visibleIndices[0] === idx

      if (isAlreadyIsolated) {
        for (let i = 0; i < datasets.length; i++) chart.show(i)
      }
      else {
        for (let i = 0; i < datasets.length; i++) {
          if (i === idx) chart.show(i)
          else chart.hide(i)
        }
      }
    }
    else {
      // Default single-click toggle behavior.
      if (chart.isDatasetVisible(idx)) chart.hide(idx)
      else chart.show(idx)
    }

    chart.update()
  }
}
